import { useEffect, useState } from 'react';
import SearchComponent from '../searchComponent';
import { getUsers, importUsers } from '../../api/user-api';
import { useSearchParams } from 'react-router-dom';
import EditUserOverlay from '../overlays/editUser';
import PendingInvitesOverlay from '../overlays/pendingInvites';
import ImportOverlay from '../overlays/csvUpload';
import { useConfig } from '../../providers/configProvider';
import { useLanguage } from '../../providers/preferenceProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faPlus, faCaretUp, faCaretDown, faUsersViewfinder } from '@fortawesome/free-solid-svg-icons';
import { ACL } from '../../acls';
import FiltersList from '../filters/filtersList';
import { getFiltersUserAdminPage } from '../../api/filter-api';

const Users = () => {
  const [showPendingInvitesOverlay, setShowPendingInvitesOverlay] = useState(false);
  const [showEditUserOverlay, setShowEditUserOverlay] = useState(false);
  const [overlayUser, setOverlayUser] = useState(null);
  const [createNew, setCreateNew] = useState(false);
  const [users, setUsers] = useState([]);
  const { isAuthorized, isCompanyManager, refresh } = useConfig();
  const pageSize = 10;
  const { t } = useLanguage();
  const [showImportOverlay, setShowImportOverlay] = useState(false);
  const [importOverlayErrors, setImportOverlayErrors] = useState([]);

  // Filters
  const [filtersList, setFiltersList] = useState([]);
  const [clear, setClear] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  // Sorting
  const currentSort = searchParams.get('users_sort') || 'name:ASC';
  const [sortKey, sortDirection] = currentSort.split(':');

  // Pagination
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  
  useEffect(() => {
    const getFilters = async () => {
      setFiltersList((await getFiltersUserAdminPage()).data);
    };
    
    getFilters();
    searchParams.set('pageSize', pageSize);
    setSearchParams(searchParams);
  }, [])

  useEffect(() => {
    fetchData(true);
    // eslint-disable-next-line
  }, [searchParams]);

  function handleAddUser() {
    setCreateNew(true);
    setOverlayUser(null);
    setShowEditUserOverlay(true);
  }

  function handleEdit(user) {
    setCreateNew(false);
    setOverlayUser(user);
    setShowEditUserOverlay(true);
  }

  function openPandingInvitesOverlay() {
    setShowPendingInvitesOverlay(true);
  }

  function closeEditUserOverlay() {
    refresh();
    setShowEditUserOverlay(false);
  }

  async function fetchData(refresh = false) {
    let localUsers = [...users];
    if (refresh) {
      localUsers = [];
    }

    const urlParams = getSearchParams(searchParams, pageSize, refresh, localUsers);
    const response = await getUsers(urlParams);

    if (response.data.rows) {
      setUsers([...localUsers, ...response.data.rows]);
      setHasMore(localUsers.length + response.data.rows.length !== response.data.count);
    } else {
      setHasMore(false);
    }
    
    setCount(response.data.count);
  }

  function getSearchParams(searchParams, pageSize, refresh, users) {
    const queryParams = new URLSearchParams();
    queryParams.append('search', searchParams.get('users_search') || '');
    queryParams.append('sort', searchParams.get('users_sort') || 'name:ASC');
    queryParams.append('pageSize', pageSize);
    queryParams.append('page', refresh ? 1 : Math.ceil(users.length / pageSize) + 1);
    if (searchParams.get('filters'))
      appendQueryParamsFromSelected(searchParams.get('filters'), queryParams);

    return queryParams;
  }

  function appendQueryParamsFromSelected(filters, queryParams) {
    const filterObject = JSON.parse(filters);
    for (const [name, items] of Object.entries(filterObject)) {
      items.forEach(item => {
        queryParams.append(name, item);
      });
    }
  }

  async function setSearch(query) {
    searchParams.set('users_search', query)
    setSearchParams(searchParams);
    fetchData(true);
  }

  async function setSort(key) {
    let newDirection = 'DESC';
    const current = searchParams.get('users_sort')?.split(':');
    if (current && current[0] === key) {  
      newDirection = current[1] === 'ASC' ? 'DESC' : 'ASC';
    }

    searchParams.set('users_sort', `${key}:${newDirection}` );
    setSearchParams(searchParams);
    fetchData(true);
  }

  function hanldeImportUsers() {
    console.log('working');
    setShowImportOverlay(true);
  }

  async function handleSave(file) {
    if (file) {
      const result = (await importUsers(file)).data;
      
      if (result?.errors || result.errors.length !== 0) {
        console.log('result', result);
        setImportOverlayErrors(result);
      }
    }
  }

  function selectedListChanged(filters){
    let filteredFilters = { ...filters };
    Object.keys(filters).filter(key => filters[key].length < 1).forEach(key => {
      delete filteredFilters[key];
    })

    mapFiltersToKeys(filteredFilters);
    searchParams.set('filters', JSON.stringify(filteredFilters));
    setSearchParams(searchParams);
  }

  function mapFiltersToKeys(filteredFilters) {
    Object.keys(filteredFilters).forEach(key => {
      filteredFilters[key] = filteredFilters[key].map(item => item.key);
    });
  }

  return (
    <>
      <div className="admin-list-wrapper">
        <div className="flex md:flex-row flex-col gap-2 mb-2 justify-between">
          <div className="flex md:flex-row flex-col gap-1">
            <SearchComponent onSearchChange={setSearch}></SearchComponent>
            <div className="flex flex-wrap">
              <FiltersList listFilters={filtersList} onSelectedChange={x => selectedListChanged(x)} clear={clear}/>
            </div>
            <button className='btn px-4 py-2' onClick={() => setClear(x => x + 1)}>
              {t.clear}
            </button>
          </div>
          <div className='md:flex-row flex-col gap-2 flex'>
            <button
              className="admin-add-button" 
              disabled={!isAuthorized(ACL.USER) && !isAuthorized(ACL.REGISTER)} 
              onClick={hanldeImportUsers}>
              {"Import " + t.users} <FontAwesomeIcon icon={faPlus} />
            </button>
            <button
              className="admin-add-button" 
              disabled={!isAuthorized(ACL.REGISTER)} 
              onClick={openPandingInvitesOverlay}>
              {t.pending_registrations} <FontAwesomeIcon icon={faUsersViewfinder} />
            </button>
            <button
              className="admin-add-button" 
              disabled={!isAuthorized(ACL.REGISTER)} 
              onClick={handleAddUser}>
              {t.add + " " + t.user} <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        </div>
        <div className="admin-list-header mb-4 flex justify-between items-center">
          <div>{t.amount_of + " " + t.users + ": " + count}</div>
          
        </div>
        <div className="admin-list-box">

          <div className="admin-list-header grid-cols-[2fr_2fr_2fr_1.5fr_1fr] px-4 py-2">
            <div onClick={() => setSort('firstname')} className="cursor-pointer select-none">
              {t.user}
              {sortKey === 'firstname' && (<FontAwesomeIcon icon={sortDirection === 'ASC' ? faCaretUp : faCaretDown} className="table-sort-icon" />)}
            </div>
            <div onClick={() => setSort('Companies.name')} className="cursor-pointer select-none">
              {t.company}
              {sortKey === 'Companies.name' && (<FontAwesomeIcon icon={sortDirection === 'ASC' ? faCaretUp : faCaretDown} className="table-sort-icon" />)}
            </div>
            <div onClick={() => setSort('email')} className="cursor-pointer select-none">
              {t.email}
              {sortKey === 'email' && (<FontAwesomeIcon icon={sortDirection === 'ASC' ? faCaretUp : faCaretDown} className="table-sort-icon" />)}
            </div>
            <div onClick={() => setSort('Role.name')} className="cursor-pointer select-none">
              {t.role}
              {sortKey === 'Role.name' && (<FontAwesomeIcon icon={sortDirection === 'ASC' ? faCaretUp : faCaretDown} className="table-sort-icon" />)}
            </div>
            <div></div>
          </div>

          {users && users.map((user, index) => (
            <div key={index} className="grid md:grid-cols-[2fr_2fr_2fr_1.5fr_1fr] items-center px-3 py-2 rounded-md m-2 bg-[var(--white-color)]">
              <div className="flex items-center gap-3 ml-1 admin-list-cell">
                <img src={user.image} alt={user.firstname + ' ' + user.lastname} className="w-10 h-10 rounded-md object-cover" />
                <span className="font-medium">{user.firstname + ' ' + user.lastname}</span>
              </div>
              <div className="admin-list-cell"><h3>{user.Company?.name}</h3></div>
              <div className="text-xs admin-list-cell" title={user.email}>{user.email}</div>
              <div className="text-sm admin-list-cell">{user.Role?.name}</div>
              <div className="text-right admin-list-cell">
                <button 
                  className="admin-edit-button"
                  disabled={!(isAuthorized(ACL.USER)) && !isCompanyManager(user)} 
                  onClick={() => handleEdit(user)}
                > 
                  {t.edit} <FontAwesomeIcon icon={faPenToSquare} />
                </button>
              </div>
            </div>
          ))}

          <div className="grid grid-cols-4 px-4 py-2">
            <div><h2>{t.showing.replace("{ph_length}", users.length).replace("{ph_count}", count)}</h2></div>
          </div>
          { hasMore && <button className="admin-edit-button" onClick={() => fetchData()}>
            {t.show_more}
          </button> }
        </div>
      </div>

      {showPendingInvitesOverlay && (
        <PendingInvitesOverlay
          isOpen={showPendingInvitesOverlay}
          onClose={() => setShowPendingInvitesOverlay(false)}
          onRefresh={fetchData}
        />
      )}

      {showEditUserOverlay && (
        <EditUserOverlay
          isOpen={showEditUserOverlay}
          onClose={closeEditUserOverlay}
          user={overlayUser}
          onRefresh={fetchData}
          createNew={createNew}
          admin
        />
      )}

      {showImportOverlay && (
        <ImportOverlay
          isOpen={showImportOverlay}
          onClose={() => setShowImportOverlay(false)}
          handleSave={handleSave}
          result={importOverlayErrors}
        />
      )}
    </>
  );
};

export default Users;

